// platformApi — typed fetchers for /v1/platform/* (Route 15).
import { apiFetch } from "../../lib/api-client.js";

export interface PlatformUser {
  id: string;
  email: string;
  display_name: string | null;
  super_admin: boolean;
  last_login_at: number | null;
  memberships: Array<{ tenant_id: string; role: string; status: string }>;
}

export interface PlatformHealth {
  tenants_active: number;
  rps_1h: number;
  line_quota: { date_utc: string; sent_today: number; monthly_cap: number };
  probes: Array<{
    service: string;
    /**
     * - `ok`       — healthy (HTTP 200 + body.ok === true)
     * - `degraded` — HTTP 200 but body did not assert `ok: true`
     * - `error`    — non-2xx or binding threw
     * - `timeout`  — exceeded the per-probe budget on the backend
     * - `unbound`  — service binding not configured for this env (config gap)
     */
    status: "ok" | "degraded" | "error" | "timeout" | "unbound";
    latency_ms: number | null;
    build_sha?: string | null;
    http_status?: number;
    detail?: string;
  }>;
  shards: Array<{ name: string; kind: string; rows_approx: number | null }>;
  queues: Array<{ name: string; depth: number | null; note?: string }>;
  correlation_id: string | null;
}

export interface PlatformAnnouncement {
  id: string;
  title: string;
  body: string;
  audience: "all_tenants" | "active_tenants" | "trial_tenants" | string;
  created_by: string | null;
  created_at: number;
  sent_at: number | null;
  reach_count: number | null;
}

export class PlatformApiError extends Error {
  status: number; code: string; correlationId: string | null; fields?: string[];
  constructor(status: number, code: string, correlationId: string | null, fields?: string[]) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId; this.fields = fields;
  }
}
export function friendlyPlatformError(err: unknown): string {
  if (err instanceof PlatformApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "只有平台管理員（super_admin）才能進行此操作。";
    if (err.status === 404 && err.code === "not_found") return "找不到這個客戶單位。";
    if (err.status === 404 && err.code === "not_found_in_tenant-api")
      return "後端代理路由尚未完成部署，請等部署 workflow 跑完後重試。";
    if (err.status === 409 && err.code === "tenant_suspended") return "這個客戶單位已停用，無法進入。";
    if (err.status === 400 && err.code === "cannot_impersonate_platform")
      return "不能代理平台本身。請選一個真實客戶單位。";
    if (err.status === 503 && err.code === "jwt_not_configured")
      return "後端尚未取得簽章金鑰，請平台管理員執行一次 bootstrap-secrets workflow（target=tenant-api）後再試。";
    if (err.status === 503) return "後端服務暫時無法回應，請稍後再試。";
    if (err.status === 400 && err.code === "invalid_line_user_id")
      return "LINE 使用者 ID 格式不對（必須以 U 開頭並有 32 個十六進位字元）。";
    if (err.status === 400 && err.code === "invalid_body")
      return `欄位填寫不完整：${(err.fields ?? []).join("、")}。`;
    // Final fallback — surface the underlying error code so support can
    // diagnose without screen-recording (the catalog above covers all the
    // known platform endpoints).
    return `平台資料暫時無法存取（${err.code}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}
async function asError(r: Response): Promise<PlatformApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id"); let fields: string[] | undefined;
  try { const b = (await r.json()) as { error?: string; correlation_id?: string; fields?: string[] }; code = b.error ?? code; cid = b.correlation_id ?? cid; fields = b.fields; } catch {}
  return new PlatformApiError(r.status, code, cid, fields);
}

export async function impersonateTenant(tenantId: string, opts?: { note?: string }): Promise<{ ok: true; tenant_id: string; tenant_name: string; expires_at: string }> {
  const r = await apiFetch(`/v1/platform/tenants/${encodeURIComponent(tenantId)}/impersonate`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ note: opts?.note ?? null }),
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { ok: true; tenant_id: string; tenant_name: string; expires_at: string };
}

/** Drop acting_tenant_id and return to "pure" super_admin mode. */
export async function clearImpersonation(): Promise<{ ok: true; previous_tenant_id: string | null }> {
  const r = await apiFetch(`/v1/platform/impersonate/clear`, {
    method: "POST", credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { ok: true; previous_tenant_id: string | null };
}

export async function listPlatformUsers(q?: string | null): Promise<{ items: PlatformUser[]; correlation_id: string | null }> {
  const qs = q ? `?q=${encodeURIComponent(q)}` : "";
  const r = await apiFetch(`/v1/platform/users${qs}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { items: PlatformUser[]; correlation_id: string | null };
}

export async function getPlatformHealth(): Promise<PlatformHealth> {
  const r = await apiFetch(`/v1/platform/health`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as PlatformHealth;
}

export async function listAnnouncements(): Promise<{ items: PlatformAnnouncement[]; correlation_id: string | null }> {
  const r = await apiFetch(`/v1/platform/announcements`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { items: PlatformAnnouncement[]; correlation_id: string | null };
}
export async function createAnnouncement(body: { title: string; body: string; audience?: string }): Promise<{ id: string }> {
  const r = await apiFetch(`/v1/platform/announcements`, {
    method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body), credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string };
}

export async function linkLineToMe(line_user_id: string): Promise<void> {
  const r = await apiFetch(`/v1/platform/me/link-line`, {
    method: "POST", headers: { "content-type": "application/json" },
    body: JSON.stringify({ line_user_id }), credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}

// ─── AI quota admin (super_admin) ───────────────────────────────────────

export interface AiGrantRow {
  id: string;
  tenant_id: string;
  amount: number;
  consumed_amount: number;
  reason: string | null;
  granted_by_user_id: string;
  granted_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  revoked_by_user_id: string | null;
  revoked_reason: string | null;
  correlation_id: string | null;
}

export interface AiChatOverrideValue {
  enabled?: boolean;
  msg_per_day_cap?: number;
  tokens_per_month_cap?: number;
  screen_context_level?: string;
}

export interface AiQuotaSnapshot {
  tenant: { id: string; name: string };
  plan: { id: string; display_name: string; cap: number | null; enabled: boolean | null };
  override: { value: AiChatOverrideValue | null; updated_at: string | null; reason: string | null };
  resolved: {
    cap: number;
    cap_source: "override" | "plan" | "default";
    enabled: boolean;
    enabled_source: "override" | "plan" | "default";
  };
  usage_this_month: number;
  grants: {
    active: AiGrantRow[];
    history: AiGrantRow[];
    remaining: number;
    granted_total: number;
    consumed_total: number;
    next_expiry_iso: string | null;
  };
  total_available: number; // -1 = unlimited
  correlation_id: string | null;
}

export async function getTenantAiQuota(tenantId: string): Promise<AiQuotaSnapshot> {
  const r = await apiFetch(`/v1/platform/tenants/${encodeURIComponent(tenantId)}/ai-quota`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as AiQuotaSnapshot;
}

export interface PutAiQuotaInput {
  /** null = remove cap override (fall through to plan); -1 = unlimited; integer >= 0 otherwise. */
  tokens_per_month_cap?: number | null;
  /** null = remove enabled override. */
  enabled?: boolean | null;
  msg_per_day_cap?: number | null;
  reason?: string | null;
}

export async function putTenantAiQuota(tenantId: string, body: PutAiQuotaInput): Promise<AiQuotaSnapshot> {
  const r = await apiFetch(`/v1/platform/tenants/${encodeURIComponent(tenantId)}/ai-quota`, {
    method: "PUT", headers: { "content-type": "application/json" },
    body: JSON.stringify(body), credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as AiQuotaSnapshot;
}

export interface IssueAiGrantInput {
  amount: number;
  reason?: string | null;
  expires_at?: string | null;
}

export async function issueAiGrant(tenantId: string, body: IssueAiGrantInput): Promise<{ grant: AiGrantRow; correlation_id: string | null }> {
  const r = await apiFetch(`/v1/platform/tenants/${encodeURIComponent(tenantId)}/ai-grants`, {
    method: "POST", headers: { "content-type": "application/json" },
    body: JSON.stringify(body), credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { grant: AiGrantRow; correlation_id: string | null };
}

export async function revokeAiGrant(tenantId: string, grantId: string, reason: string | null): Promise<void> {
  const r = await apiFetch(`/v1/platform/tenants/${encodeURIComponent(tenantId)}/ai-grants/${encodeURIComponent(grantId)}`, {
    method: "DELETE", headers: { "content-type": "application/json" },
    body: JSON.stringify({ reason }), credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}
